import React from "react";
import PublicLayout from "../components/PublicLayout";
import Seo from "../components/Seo";
import InfinityMotif from "../components/InfinityMotif";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Handshake,
  Lightning,
  Eye,
  ShieldCheck,
  Star,
  Buildings,
  Briefcase,
  Storefront,
  Globe,
} from "@phosphor-icons/react";

const VALUES = [
  { icon: Eye, title: "Transparency", copy: "No hidden carrier incentives, no opaque pricing. You see what we see." },
  { icon: Lightning, title: "Speed", copy: "Decisions in hours, not weeks. We answer on the second ring." },
  { icon: ShieldCheck, title: "Accountability", copy: "One team, one contact, one person who owns your outcome." },
  { icon: Handshake, title: "Partnership", copy: "Clients stay because we grow with them — from startup to JSE-listed." },
];

const SECTORS = [
  { icon: Storefront, label: "SoHo" },
  { icon: Briefcase, label: "SMMEs" },
  { icon: Buildings, label: "Enterprise" },
  { icon: Globe, label: "Public Sector" },
  { icon: Handshake, label: "JSE-Listed" },
];

export default function AboutPage() {
  return (
    <PublicLayout>
      <Seo
        title="About — 50+ years of combined ICT experience"
        description="Infinity DMS — backed by directors with 50+ years of combined ICT experience. Approved partner for MTN, Vodacom, Cell C, Vox and Telkom."
        path="/about"
      />
      <section className="relative pt-20 pb-24 overflow-hidden text-center" data-testid="about-page">
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-[90vw] h-[320px] rainbow-swoosh pointer-events-none" />
        <InfinityMotif variant="wireframe" className="absolute top-2 left-1/2 -translate-x-1/2 w-[1100px] max-w-[120vw] h-auto" opacity={0.14} />
        <div className="relative max-w-[1000px] mx-auto px-6">
          <div className="flex justify-center mb-7">
            <Star size={24} weight="fill" className="text-[#dda632]" />
          </div>
          <div className="mx-auto mb-8 w-32 h-[5px] rounded-full bg-gradient-to-r from-[#55a2d2] via-[#af3fa6] to-[#dda632]" />
          <div className="eyebrow mb-5">Studio — Inside Infinity</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.4rem] leading-[0.98]">
            50 years of ICT.<br />
            <span className="rainbow-text">One trusted partner.</span>
          </h1>
          <p className="mt-10 text-xl text-[#a8b2cc] leading-relaxed max-w-2xl mx-auto">
            Infinity Data &amp; Mobile Solutions has been a trusted leader in telecommunications and
            IT since 2020 — backed by directors with over <strong className="text-[#f5ecd9]">50 years of combined
            ICT experience</strong>. We're an approved partner for South Africa's leading network
            providers, delivering innovative, high-performance solutions tailored to every scale of
            business.
          </p>
        </div>
      </section>

      <div className="rainbow-bar rainbow-bar-xl" />

      {/* Vision / Mission */}
      <section className="py-28">
        <div className="max-w-[1080px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="soft-card p-10">
            <div className="eyebrow mb-4">Our Vision</div>
            <h2 className="font-display text-3xl text-[#f5ecd9] leading-[1.08]">
              Seamless, scalable, <span className="rainbow-text">cutting-edge</span> connectivity for every South African business.
            </h2>
            <p className="mt-6 text-[#a8b2cc] leading-relaxed">
              We partner with the networks that matter — MTN, Vodacom, Cell C, Vox and Telkom — and
              layer on the cloud, security and automation tools that turn connectivity into
              competitive advantage.
            </p>
          </div>
          <div className="soft-card p-10 bg-[#141e32] text-white border-transparent">
            <div className="eyebrow mb-4" style={{ color: "#dda632" }}>Our Mission</div>
            <h2 className="font-display text-3xl leading-[1.08]">
              To be your <span className="rainbow-text">end-to-end</span> connectivity partner.
            </h2>
            <ul className="mt-6 space-y-2 text-[#a8b2cc]">
              <li>— Enhance operational efficiency</li>
              <li>— Boost team productivity</li>
              <li>— Reduce costs through smart tech investment</li>
            </ul>
          </div>
        </div>
      </section>

      <div className="rainbow-bar rainbow-bar-xl" />

      {/* Sectors we serve */}
      <section className="py-28 bg-[#141e32]">
        <div className="max-w-[1080px] mx-auto px-6 text-center">
          <div className="eyebrow mb-5">Who We Serve</div>
          <h2 className="font-display text-4xl lg:text-5xl text-[#f5ecd9] leading-[1.05] max-w-2xl mx-auto">
            From SoHo to <span className="rainbow-text">JSE-listed.</span>
          </h2>
          <p className="mt-6 text-lg text-[#a8b2cc] max-w-xl mx-auto">
            We build tailored solutions for every scale of business, all delivered by the same
            accountable team.
          </p>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-5 gap-5">
            {SECTORS.map(({ icon: Icon, label }) => (
              <div key={label} className="bg-[#141e32] rounded-[28px] p-6 flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-[#070b14] flex items-center justify-center">
                  <Icon size={24} weight="duotone" className="text-[#f5ecd9]" />
                </div>
                <div className="font-display text-lg">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-28">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="eyebrow mb-4">What we stand for</div>
            <h2 className="font-display text-4xl lg:text-5xl text-[#f5ecd9] leading-[1.05]">
              Four values. <span className="rainbow-text">Zero compromises.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {VALUES.map((v, idx) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="soft-card p-8 text-center" data-testid={`value-card-${idx}`}>
                  <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-[#141e32] mb-6">
                    <Icon size={24} weight="duotone" className="text-[#f5ecd9]" />
                  </div>
                  <h3 className="font-display text-2xl mb-3">{v.title}</h3>
                  <p className="text-[#a8b2cc] leading-relaxed">{v.copy}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 bg-[#141e32] text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 rainbow-bar rainbow-bar-xl" />
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <div className="eyebrow mb-5" style={{ color: "#dda632" }}>Let's talk</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.8rem] leading-[1.02]">
            Your stack reviewed in<br />
            <span className="rainbow-text">48 hours.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#f5ecd9] text-[#070b14] rounded-full px-7 py-4 font-mono-accent text-[0.78rem] tracking-[0.22em] uppercase font-semibold hover:bg-[#dda632]"
              data-testid="about-cta-contact"
            >
              Start the Audit <ArrowUpRight size={14} weight="bold" />
            </Link>
            <a
              href="tel:0718889055"
              className="inline-flex items-center gap-2 border border-white/40 text-white rounded-full px-7 py-4 font-mono-accent text-[0.78rem] tracking-[0.22em] uppercase font-semibold hover:bg-[#f5ecd9] hover:text-[#070b14] transition-colors"
              data-testid="about-cta-phone"
            >
              071 888 9055
            </a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
